import { useCallback, useEffect, useRef, useState } from 'react';
import { G } from './icons.tsx';
import { useT } from '../i18n/I18n.tsx';

/**
 * Message composer.
 *
 * The familiar behaviour: the trailing control is a microphone until there is
 * text, then a send button. Attachment and emoji menus open above the bar. The
 * textarea grows with the message and stops at a sensible height.
 *
 * Only actions the product genuinely supports are offered. There is no camera
 * capture and no contact sharing here, because neither exists — a menu item
 * that does nothing is worse than an absent one.
 */

const EMOJI = [
  '😀',
  '😃',
  '😄',
  '😁',
  '😅',
  '😂',
  '🙂',
  '😉',
  '😊',
  '😍',
  '😘',
  '😗',
  '🙃',
  '🤗',
  '🤔',
  '😐',
  '😴',
  '😷',
  '🤒',
  '🥳',
  '😎',
  '🤓',
  '😕',
  '😢',
  '😭',
  '😤',
  '😡',
  '👍',
  '👎',
  '👌',
  '🙏',
  '💪',
  '👋',
  '🎉',
  '✨',
  '🔥',
  '💯',
  '❤️',
  '🧡',
  '💛',
  '💚',
  '💙',
  '💜',
  '✅',
  '❌',
  '⚠️',
  '📎',
  '📷',
];

export interface ComposerProps {
  disabled?: boolean;
  disabledReason?: string;
  onSendText: (body: string) => Promise<void> | void;
  onSendFiles: (files: FileList) => Promise<void> | void;
  onRecordVoice: (blob: Blob, seconds: number) => void;
  onTyping: (active: boolean) => void;
}

export function Composer({
  disabled,
  disabledReason,
  onSendText,
  onSendFiles,
  onRecordVoice,
  onTyping,
}: ComposerProps) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const [menu, setMenu] = useState<'none' | 'attach' | 'emoji'>('none');
  const textRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRef = useRef<HTMLInputElement>(null);

  const hasText = draft.trim().length > 0;

  // Grow with the message, up to the cap the stylesheet sets.
  useEffect(() => {
    const node = textRef.current;
    if (!node) return;
    // Reset first: scrollHeight only shrinks if the element is allowed to.
    node.style.height = '20px';
    if (node.value.length > 0) {
      node.style.height = `${Math.min(node.scrollHeight, 100)}px`;
    }
  }, [draft]);

  useEffect(() => {
    if (menu === 'none') return;
    const close = () => setMenu('none');
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menu]);

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    onTyping(false);
    await onSendText(body);
    textRef.current?.focus();
  }, [draft, onSendText, onTyping]);

  const recorder = useVoiceRecorder(onRecordVoice);

  if (recorder.recording) {
    return (
      <div className="composer">
        <button
          type="button"
          className="ibtn"
          onClick={recorder.cancel}
          aria-label={t('composer.cancelRecording')}
        >
          <G n="trash" size={22} />
        </button>
        <div className="composer__recording">
          <span className="rec-dot" aria-hidden="true" />
          {t('composer.recording')} · {String(Math.floor(recorder.seconds / 60))}:
          {String(recorder.seconds % 60).padStart(2, '0')}
        </div>
        <button
          type="button"
          className="ibtn composer__send"
          onClick={recorder.stop}
          aria-label={t('composer.stopAndSend')}
        >
          <G n="send" size={22} />
        </button>
      </div>
    );
  }

  return (
    <form
      className="composer"
      onSubmit={(event) => {
        event.preventDefault();
        void send();
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={menu === 'emoji' ? 'ibtn is-active' : 'ibtn'}
        onClick={() => setMenu(menu === 'emoji' ? 'none' : 'emoji')}
        aria-label={t('composer.emoji')}
        aria-expanded={menu === 'emoji'}
        disabled={disabled}
      >
        <G n="emoji" size={25} />
      </button>

      <button
        type="button"
        className={menu === 'attach' ? 'ibtn is-active' : 'ibtn'}
        onClick={() => setMenu(menu === 'attach' ? 'none' : 'attach')}
        aria-label={t('composer.attach')}
        aria-expanded={menu === 'attach'}
        disabled={disabled}
      >
        <G n="attach" size={25} />
      </button>

      {menu === 'emoji' ? (
        <div className="emoji-panel" role="menu" aria-label={t('composer.emoji')}>
          {EMOJI.map((glyph) => (
            <button
              key={glyph}
              type="button"
              onClick={() => {
                setDraft((value) => value + glyph);
                textRef.current?.focus();
              }}
            >
              {glyph}
            </button>
          ))}
        </div>
      ) : null}

      {menu === 'attach' ? (
        <div className="attach-menu" role="menu" aria-label={t('composer.attach')}>
          <button type="button" onClick={() => mediaRef.current?.click()}>
            <span className="attach-menu__icon" style={{ color: '#bf59cf' }}>
              <G n="image" size={20} />
            </span>
            {t('composer.photosVideos')}
          </button>
          <button type="button" onClick={() => fileRef.current?.click()}>
            <span className="attach-menu__icon" style={{ color: '#5157ae' }}>
              <G n="document" size={20} />
            </span>
            {t('composer.document')}
          </button>
        </div>
      ) : null}

      <input
        ref={fileRef}
        id="attachment-input"
        type="file"
        multiple
        className="visually-hidden"
        onChange={(event) => {
          if (event.target.files?.length) void onSendFiles(event.target.files);
          event.target.value = '';
          setMenu('none');
        }}
      />
      <input
        ref={mediaRef}
        id="media-input"
        type="file"
        multiple
        accept="image/*,video/*"
        className="visually-hidden"
        onChange={(event) => {
          if (event.target.files?.length) void onSendFiles(event.target.files);
          event.target.value = '';
          setMenu('none');
        }}
      />

      <div className="composer__field">
        <textarea
          ref={textRef}
          rows={1}
          value={draft}
          disabled={disabled}
          aria-label="Message"
          placeholder={
            disabled ? (disabledReason ?? t('composer.disabled')) : t('composer.placeholder')
          }
          onChange={(event) => {
            setDraft(event.target.value);
            onTyping(event.target.value.length > 0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void send();
            }
          }}
        />
      </div>

      {hasText ? (
        <button
          type="submit"
          className="ibtn composer__send"
          aria-label={t('composer.send')}
          disabled={disabled}
        >
          <G n="send" size={22} />
        </button>
      ) : (
        <button
          type="button"
          className="ibtn"
          aria-label={t('composer.record')}
          disabled={disabled || !recorder.supported}
          onClick={() => void recorder.start()}
        >
          <G n="mic" size={24} />
        </button>
      )}
    </form>
  );
}

/** Recording state, kept out of the component body. */
function useVoiceRecorder(onRecorded: (blob: Blob, seconds: number) => void) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedRef = useRef(0);
  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const supported =
    typeof window !== 'undefined' &&
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function';

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const start = useCallback(async () => {
    if (!supported) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;
      startedRef.current = Date.now();

      recorder.ondataavailable = (event) => chunksRef.current.push(event.data);
      recorder.onstop = () => {
        stopTimer();
        for (const track of stream.getTracks()) track.stop();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (!cancelledRef.current && blob.size > 0) {
          onRecorded(blob, (Date.now() - startedRef.current) / 1000);
        }
        setRecording(false);
        setSeconds(0);
      };

      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(
        () => setSeconds(Math.floor((Date.now() - startedRef.current) / 1000)),
        250,
      );
    } catch {
      setRecording(false);
    }
  }, [onRecorded, supported]);

  return {
    supported,
    recording,
    seconds,
    start,
    stop: () => recorderRef.current?.stop(),
    cancel: () => {
      cancelledRef.current = true;
      recorderRef.current?.stop();
    },
  };
}
